import { flatten, orderBy } from "lodash";

import {
  INSURANCE_PAYMENT_SOURCE,
  MANAGED_CARE_PAYMENT_SOURCE,
  PRIVATE_PAY_PAYMENT_SOURCE
} from "constants/patients";
import { AppointmentResponse } from "services/types/appointments.type";
import { InsurancePolicy } from "services/types/insurance-policies.type";
import { ManagedCarePlan } from "services/types/managed-care-plans.type";
import { SaleItem } from "services/types/sale-items.type";
import {
  LineItem,
  LineItemInsurer,
  Sale,
  SalesOverview
} from "services/types/sales.type";

import { convertAppointmentResponseToModel } from "./appointments.util";
import { formatClinic } from "./clinics.util";
import {
  formatDateOnlyValue,
  formatStaffMemberFullName,
  roundToTwo
} from "./common";
import { formatPatient } from "./patients.util";
import { recalculateProductsPricesWithDistribution } from "./products-requests.utils";

export const formatSale = (
  {
    appointment,
    patient,
    clinic,
    provider,
    supervising_provider,
    ...sale
  }: Sale,
  overview?: SalesOverview
): Sale & { overview?: SalesOverview } => ({
  ...sale,
  patient: patient && formatPatient(patient),
  clinic: clinic && formatClinic(clinic),
  appointment:
    appointment &&
    convertAppointmentResponseToModel(appointment as AppointmentResponse),
  provider: provider && formatStaffMemberFullName(provider),
  supervising_provider:
    supervising_provider && formatStaffMemberFullName(supervising_provider),
  overview: overview
});

export const getSalePaymentSourceDisplayText = ({
  payment_source_type,
  insurance_policy,
  secondary_insurance_policy,
  managed_care_plan
}: {
  payment_source_type?: number;
  insurance_policy?: Partial<InsurancePolicy> | null;
  secondary_insurance_policy?: Partial<InsurancePolicy> | null;
  managed_care_plan?: Partial<ManagedCarePlan> | null;
}) => {
  if (payment_source_type === PRIVATE_PAY_PAYMENT_SOURCE) {
    return {
      label: "Private Pay"
    };
  } else if (payment_source_type === MANAGED_CARE_PAYMENT_SOURCE) {
    return {
      title: "Managed Care",
      label: managed_care_plan?.name
        ? `${managed_care_plan.name} (${managed_care_plan?.managed_care_provider?.name})`
        : ""
    };
  } else if (payment_source_type === INSURANCE_PAYMENT_SOURCE) {
    const insurances = [
      insurance_policy?.insurer?.name ?? "",
      secondary_insurance_policy?.insurer?.name ?? ""
    ].filter(Boolean);
    const text = {
      title: insurances.length > 1 ? "Insurance Policies" : "Insurance Policy",
      label: insurances.join(", ")
    };
    if (!text.label) {
      return {
        label: "Insurance"
      };
    } else {
      return text;
    }
  }
  return {
    title: "",
    label: ""
  };
};

export const recalculateSaleItemInsurerAmounts = (
  total: number,
  insurers: LineItemInsurer[] | undefined = undefined
): LineItemInsurer[] | null => {
  if (!insurers) return null;
  const oldAmounts = insurers?.map((insurer) => insurer.amount ?? "0") ?? [];

  if (oldAmounts.length && oldAmounts.every((amount) => !Number(amount))) {
    oldAmounts[0] = roundToTwo(total).toFixed(2);
  }

  const result = recalculateProductsPricesWithDistribution(
    oldAmounts,
    total.toFixed(2)
  ).map((amount, index) => ({
    ...insurers[index],
    amount: roundToTwo(amount).toFixed(2)
  }));

  return result;
};

export function getSaleItemResponsibility(
  total: number,
  payment_source_type: number | null | undefined | "",
  insurers: LineItemInsurer[] | undefined = undefined,
  multipleInsurersFeature: boolean
) {
  if (
    !payment_source_type ||
    payment_source_type.toString() === PRIVATE_PAY_PAYMENT_SOURCE.toString()
  ) {
    return {
      patient_responsibility: roundToTwo(total || 0).toFixed(2),
      insurer_responsibility: "0.00",
      insurers: multipleInsurersFeature
        ? recalculateSaleItemInsurerAmounts(roundToTwo(total || 0), insurers)
        : undefined
    };
  }
  return {
    patient_responsibility: "0.00",
    insurer_responsibility: roundToTwo(total || 0).toFixed(2),
    insurers: multipleInsurersFeature
      ? recalculateSaleItemInsurerAmounts(roundToTwo(total || 0), insurers)
      : undefined
  };
}

export function getDistinctSaleItemsInsurers({
  items,
  policies,
  managed_care_plans,
  content_type = "insurance_policy"
}: {
  items: LineItem[] | undefined;
  policies: InsurancePolicy[] | undefined;
  managed_care_plans?: ManagedCarePlan[];
  content_type?: string;
}) {
  const idAmountMap = new Map<number, number>();

  flatten(
    items?.map((item: LineItem) =>
      (item.insurers ?? [])
        .filter((insurer) =>
          content_type === undefined
            ? true
            : insurer.content_type === content_type
        )
        .map((insurer) => ({
          id: insurer.object_id ?? insurer.insurer?.id,
          amount: Number(insurer.amount ?? "0")
        }))
    )
  ).forEach((idAmount) => {
    if (idAmountMap.has(idAmount.id)) {
      idAmountMap.set(
        idAmount.id,
        idAmount.amount + Number(idAmountMap.get(idAmount.id))
      );
    } else {
      idAmountMap.set(idAmount.id, idAmount.amount);
    }
  });

  const managedCarePlans: Array<ManagedCarePlan & { amount: number }> =
    managed_care_plans
      ?.filter((plan) => idAmountMap.has(plan.id))
      .map((plan) => ({
        ...plan,
        amount: Number(idAmountMap.get(plan.id))
      })) ?? [];

  const insurancePolicies: Array<InsurancePolicy & { amount: number }> =
    policies
      ?.filter((policy) => idAmountMap.has(policy.id))
      .map((policy) => ({
        ...policy,
        amount: Number(idAmountMap.get(policy.id))
      })) ?? [];

  return {
    managedCarePlans: orderBy(managedCarePlans, ["amount"], ["desc"]),
    insurancePolicies: orderBy(
      insurancePolicies,
      ["amount", "type"],
      ["desc", "asc"]
    )
  };
}

export function formatEditSaleItem(
  item: SaleItem | LineItem,
  features?: {
    salesDeliveredAtFeature?: boolean;
    discountTypesFeature?: boolean;
    priceTablesFeature?: boolean;
  }
) {
  const { salesDeliveredAtFeature, discountTypesFeature } = features ?? {};

  const result = {
    ...item,
    original_quantity: item.quantity ?? 0,
    clinic_tax: item?.clinic_tax?.id,
    discount_type: discountTypesFeature ? item.discount_type || null : null,
    delivered_at:
      salesDeliveredAtFeature && item.delivered_at
        ? formatDateOnlyValue(item.delivered_at)
        : null,
    insurers:
      "insurers" in item
        ? item?.insurers?.map((insurer) => ({
            amount: roundToTwo(parseFloat(String(insurer.amount ?? 0))).toFixed(
              2
            ),
            content_type: insurer?.content_type ?? "insurance_policy",
            object_id: insurer?.object_id ?? insurer?.insurer?.id
          }))
        : undefined
  };

  if (Boolean(item.returned_at || item.exchanged_at)) {
    return {
      ...result,
      patient_responsibility: "0.00",
      insurer_responsibility: "0.00"
    };
  }

  return result;
}

export const isEmptyInsurers = (items: any) =>
  !flatten(items.map((item: any) => item.insurers)).filter(
    (item: any) => item && item.content_type !== "managed_care_plan"
  ).length;

export const buildSaleItemInsurersArray = ({
  managedCarePlan,
  insurancePolicy,
  secondaryInsurancePolicy,
  total
}: {
  managedCarePlan?: ManagedCarePlan;
  insurancePolicy?: InsurancePolicy;
  secondaryInsurancePolicy?: InsurancePolicy;
  total?: number | string | null;
}) => {
  const insurers: LineItemInsurer[] = [];

  if (managedCarePlan) {
    insurers.push({
      content_type: "managed_care_plan",
      object_id: managedCarePlan.id,
      amount: roundToTwo(parseFloat(String(total ?? 0))).toFixed(2)
    });
  } else if (insurancePolicy) {
    insurers.push({
      content_type: "insurance_policy",
      object_id: insurancePolicy.id,
      amount: roundToTwo(parseFloat(String(total ?? 0))).toFixed(2)
    });

    if (secondaryInsurancePolicy?.id) {
      insurers.push({
        content_type: "insurance_policy",
        object_id: secondaryInsurancePolicy.id,
        amount: String(0)
      });
    }
  }

  return insurers;
};
